import { FC, useState, useEffect, useRef } from 'react';
import { Country, Disease } from '../database';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Syringe, Info, Shield, AlertCircle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLocalizedPath } from '../utils/urlLocalization';
import Card from '../components/ui/Card';

interface CountryInfoProps {
  country: Country;
  diseases: Disease[];
  onBack: () => void;
}

const CountryInfo: FC<CountryInfoProps> = ({ country, diseases, onBack }) => {
  const { t, i18n } = useTranslation();
  const [postalCode, setPostalCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const geoTriedRef = useRef(false);

  useEffect(() => {
    const saved = localStorage.getItem('userLocation');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPostalCode(parsed.postalCode || '');
        setCountryName(parsed.countryName || '');
        setLocationAllowed(true);
      } catch {
        setShowModal(true);
      }
    } else {
      setShowModal(true);
    }
  }, []);

  // Automatyczne wywołanie geolokalizacji po otwarciu modala (tylko raz na otwarcie)
  useEffect(() => {
    if (showModal && !geoTriedRef.current) {
      geoTriedRef.current = true;
      setGeoLoading(true);
      setGeoError('');
      if (!navigator.geolocation) {
        setGeoError('Geolokalizacja nie jest obsługiwana przez tę przeglądarkę.');
        setGeoLoading(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
          const data = await res.json();
          setPostalCode(data.address.postcode || '');
          setCountryName(data.address.country || '');
        } catch {
          setGeoError('Nie udało się pobrać lokalizacji.');
        }
        setGeoLoading(false);
      }, (err) => {
        if (err.code === 1) {
          setGeoError(t('modal.geoErrorDenied'));
        } else {
          setGeoError(t('modal.geoErrorGeneric', { message: err.message }));
        }
        setGeoLoading(false);
      });
    }
    if (!showModal) {
      geoTriedRef.current = false;
    }
  }, [showModal]);

  const handleLocationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem('userLocation', JSON.stringify({ postalCode, countryName }));
    setLocationAllowed(true);
    setShowModal(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Modal do wpisania lokalizacji */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-sm w-full relative">
            <button onClick={() => setShowModal(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">✕</button>
            <h2 className="text-xl font-bold mb-4">{t('modal.enterLocation')}</h2>
            <form onSubmit={handleLocationSubmit} className="space-y-4">
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">{t('modal.postalCode')}</label>
                <input
                  id="postalCode"
                  type="text"
                  value={postalCode}
                  onChange={e => setPostalCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="countryName" className="block text-sm font-medium text-gray-700 mb-1">{t('modal.country')}</label>
                <input
                  id="countryName"
                  type="text"
                  value={countryName}
                  onChange={e => setCountryName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors w-full"
                disabled={geoLoading}
              >
                {geoLoading ? t('modal.loading') : t('modal.saveLocation')}
              </button>
            </form>
            {geoError && <div className="text-red-600 text-xs mt-2">{geoError}</div>}
            <div className="text-xs text-gray-500 mt-4">{t('modal.info')}</div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="mb-6 px-6 py-3 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center text-blue-600 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 transform hover:-translate-x-1"
        >
          <ArrowLeft className="w-5 h-5 mr-3" />
          <span className="font-medium">{t('home.countryInfo.backToMap')}</span>
        </button>
        <Link
          to={getLocalizedPath('searchClinics', i18n.language)}
          title={t('nav.searchClinics')}
          aria-label="Search Clinics"
          className="mb-6 px-6 py-3 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center text-blue-600 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 transform hover:-translate-x-1"
        >
          <span className="font-medium">{t('nav.searchClinics')}</span>
          <ArrowRight className="w-5 h-5 ml-3" />
        </Link>
      </div>

      <Card className="shadow-xl p-8 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Syringe className="w-8 h-8 text-blue-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-800">
              {t('home.countryInfo.vaccinationsFor')}: {country.name}
            </h2>
          </div>
        </div>

        <div className="mb-6 p-4 bg-blue-50 rounded-xl flex items-start">
          <Info className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-base text-blue-700 leading-relaxed">
            {t('home.countryInfo.disclaimer')}
          </p>
        </div>
        <div className="space-y-4">
          {diseases.map((disease, index) => (
            <div
              key={index}
              className="border rounded-lg p-4 hover:border-blue-200 transition-colors hover:shadow-md"
            >
              <div className="flex items-start">
                {disease.required ? (
                  <Shield className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-1 mr-3 flex-shrink-0" />
                )}
                <div>
                  <div className="flex items-center">
                    <h3 className="font-semibold text-gray-800">
                      {disease.name}
                    </h3>
                    {disease.required && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        {t('home.countryInfo.disease.required')}
                      </span>
                    )}
                    <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${disease.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                      disease.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {t('home.countryInfo.disease.riskLevel')}: {disease.risk_level === 'low' ? 'niskie' :
                        disease.risk_level === 'medium' ? 'średnie' : 'wysokie'}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{disease.description}</p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Check className="w-4 h-4 mr-1" />
                      {t('home.countryInfo.disease.validity')}: {disease.validity ?? "Brak danych"}
                      {disease.validity ? <span className="text-xs text-gray-400 ml-2">({t('home.countryInfo.disease.different')})</span> : ""}
                    </div>
                    <div className="text-sm text-gray-500">
                      <strong>{t('home.countryInfo.disease.sideEffects')}:</strong>
                      <ul className="list-disc list-inside ml-2 mt-1">
                        {disease.side_effects ? disease.side_effects.map((effect, idx) => (
                          <li key={idx}>{effect}</li>
                        )) : ""}
                      </ul>
                    </div>
                    <div className="text-sm text-gray-500">
                      <strong>{t('home.countryInfo.disease.price')}:</strong> {locationAllowed ? disease.price : <button onClick={() => setShowModal(true)} className="text-blue-600 underline">{t('modal.provideLocationToSeePrice')}</button>}
                      <span className="text-xs text-gray-400 ml-2">{t('home.countryInfo.disease.priceNote')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default CountryInfo;
